'use client'
import type { ReactNode } from 'react'
import { Button, ButtonGroup } from '@chakra-ui/react'
import type { Control, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form'
import {
  FormDescription,
  FormField,
  FormFieldControl,
  FormFieldLabel,
  FormMessage
} from '../FormField'

interface SegmentOption {
  value: string
  label: string
}

interface GrandFormSegmentedFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> {
  name: TName
  label?: ReactNode
  description?: ReactNode
  tooltip?: ReactNode
  /** Segments rendered left-to-right; the field stores the selected `value`. */
  options: SegmentOption[]
  required?: boolean
  optional?: boolean
  disabled?: boolean
  size?: 'xs' | 'sm' | 'md'
  rules?: RegisterOptions<TFieldValues, TName>
  control?: Control<TFieldValues>
}

/**
 * Attached segmented control bound to a string form value — e.g. a tri-state
 * boolean (`'' | 'true' | 'false'` for inherit / on / off) so an optional setting
 * can be reset to its default rather than being stuck on true/false.
 */
export const GrandFormSegmentedField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  name,
  label,
  description,
  tooltip,
  options,
  required,
  optional,
  disabled,
  size = 'sm',
  rules,
  control
}: GrandFormSegmentedFieldProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={control}
      name={name}
      optional={optional}
      required={required}
      rules={rules}
      render={({ field }) => (
        <FormFieldControl disabled={disabled}>
          {label ? <FormFieldLabel tooltip={tooltip}>{label}</FormFieldLabel> : null}

          <ButtonGroup isAttached size={size} variant="outline" isDisabled={disabled}>
            {options.map((option) => {
              // Coalesce nullish to '' so an unset value selects the first ('Default') segment.
              const isSelected = (field.value ?? '') === option.value
              return (
                <Button
                  key={option.value}
                  type="button"
                  variant={isSelected ? 'solid' : 'outline'}
                  colorScheme={isSelected ? 'blue' : 'gray'}
                  onClick={() => field.onChange(option.value)}
                >
                  {option.label}
                </Button>
              )
            })}
          </ButtonGroup>

          {description ? <FormDescription>{description}</FormDescription> : null}

          <FormMessage />
        </FormFieldControl>
      )}
    />
  )
}
